import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { useMediaQuery } from '@mui/material';
import {
  FileViewerStateAtom,
  INIT_VIEWER_SETTINGS,
} from '@/recoil/atom/fileViewer';
import { detectFileType } from '@/components/FileViewer/utils/fileTypeDetector';
import { PAGE_URL } from '@/constant/url';

export type OpenFileViewerOptions = {
  uuid?: string;
  fileUuid?: string;
  name: string;
  link: string;
  openAiPanel?: boolean;
};

export const useFileViewer = () => {
  const setViewerState = useSetAtom(FileViewerStateAtom);
  const isMobile = useMediaQuery('(max-width:768px)');

  const openFileViewer = useCallback(
    ({
      uuid,
      fileUuid,
      name,
      link,
      openAiPanel = false,
    }: OpenFileViewerOptions) => {
      const resolvedUuid = uuid ?? fileUuid ?? '';

      // PC: 새 탭으로 열기
      if (!isMobile) {
        const params = new URLSearchParams({
          link: encodeURIComponent(link),
          name: encodeURIComponent(name),
          uuid: resolvedUuid,
          openAiPanel: String(openAiPanel),
        });
        window.open(`${PAGE_URL.FILE_VIEWER}?${params.toString()}`, '_blank');
        return;
      }

      // 모바일: 기존 모달 방식 유지.
      // 모바일에서는 AI 챗을 기본으로 열지 않는다(사용자가 GNB 별 아이콘으로 직접 연다).
      const fileType = detectFileType(name);
      setViewerState({
        isOpen: true,
        file: {
          link,
          name,
          type: fileType,
          uuid: resolvedUuid,
        },
        settings: INIT_VIEWER_SETTINGS,
        isAiPanelOpen: false,
      });
    },
    [setViewerState, isMobile],
  );

  const closeFileViewer = useCallback(() => {
    setViewerState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, [setViewerState]);

  return {
    openFileViewer,
    closeFileViewer,
  };
};
